import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { axiosInstance as axios } from '../context/AuthContext';
import { useFeatureGate } from '../components/FeatureGate';
import { 
  Check, X, Crown, Zap, Star, Rocket, 
  CreditCard, Shield, ChevronRight, Loader2 
} from 'lucide-react';
import { toast } from 'sonner';

import { useAuth } from '../context/AuthContext';
import { FoundingMemberBadge } from '../components/FoundingMemberBadge';

const API = '';

const tierIcons = {
  free: Star,
  seedling: Zap,
  growing: Crown,
  flourishing: Rocket
};

const tierColors = {
  free: 'gray',
  seedling: 'emerald',
  growing: 'blue',
  flourishing: 'amber'
};

const tierEmojis = {
  free: '⭐',
  seedling: '🌱',
  growing: '🌿',
  flourishing: '🌳'
};

export default function Subscription({ isPublic = false }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshSubscription } = useFeatureGate();
  const { user } = useAuth();
  const [tiers, setTiers] = useState(null);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [processingTier, setProcessingTier] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const tiersRes = await axios.get(`${API}/api/subscriptions/tiers`);
      setTiers(tiersRes.data.tiers);
      
      if (!isPublic) {
        const [subRes, usageRes] = await Promise.all([
          axios.get(`${API}/api/subscriptions/current`, { withCredentials: true }),
          axios.get(`${API}/api/subscriptions/usage`, { withCredentials: true })
        ]);
        setCurrentSubscription(subRes.data);
        setUsage(usageRes.data);
      }
    } catch (error) {
      console.error('Error fetching subscription data:', error);
      if (!isPublic) {
        toast.error('Failed to load subscription information');
      }
    } finally {
      setLoading(false);
    }
  }, [isPublic]);

  useEffect(() => {
    fetchData();
    
    if (!isPublic) {
      const sessionId = searchParams.get('session_id');
      if (sessionId) {
        toast.success('Subscription Upgraded!', {
          description: 'Your new features are now available.',
          duration: 6000,
        });
        if (refreshSubscription) {
          refreshSubscription();
        }
        navigate('/subscription', { replace: true });
      }
    }
  }, [isPublic, fetchData, searchParams, refreshSubscription, navigate]);

  const handleSubscribe = async (tierKey) => {
    // For public view, redirect to register
    if (isPublic) {
      navigate(`/register?plan=${tierKey}`);
      return;
    }
    
    if (tierKey === 'free' || tierKey === currentSubscription?.tier) return;
    
    setProcessingTier(tierKey);
    try {
      const response = await axios.post(
        `${API}/api/subscriptions/create-checkout-session`,
        {
          tier: tierKey,
          billing_cycle: billingCycle,
          success_url: `${window.location.origin}/subscription`,
          cancel_url: `${window.location.origin}/subscription`
        },
        { withCredentials: true }
      );
      
      // Redirect to Stripe Checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      toast.error('Failed to start checkout process');
      setProcessingTier(null);
    }
  };

  const handleCancelSubscription = async () => {
    if (!confirm('Are you sure you want to cancel? You\'ll retain access until the end of your billing period.')) {
      return;
    }
    
    try {
      await axios.post(`${API}/api/subscriptions/cancel`, {}, { withCredentials: true });
      toast.success('Subscription will be canceled at period end');
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel subscription');
    }
  };

  const handleReactivate = async () => {
    try {
      await axios.post(`${API}/api/subscriptions/reactivate`, {}, { withCredentials: true });
      toast.success('Subscription reactivated!');
      fetchData();
    } catch (error) {
      toast.error('Failed to reactivate subscription');
    }
  };

  const formatLimit = (value) => {
    if (value === -1) return 'Unlimited';
    return value.toLocaleString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const tierOrder = ['free', 'seedling', 'growing', 'flourishing'];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation Header for Public View */}
      {isPublic && (
        <header className="bg-white shadow-sm sticky top-0 z-10">
          <div className="max-w-6xl mx-auto px-4 py-4 flex justify-between items-center">
            <button
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 text-gray-800 hover:text-blue-600 transition"
              data-testid="back-to-home-btn"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span className="font-semibold text-lg">Legacy Forge Family</span>
            </button>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/login')}
                className="px-4 py-2 text-blue-600 hover:text-blue-700 font-medium"
              >
                Log In
              </button>
              <button
                onClick={() => navigate('/register')}
                className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
              >
                Sign Up
              </button>
            </div>
          </div>
        </header>
      )}
      
      <div className="py-12 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Choose Your Plan</h1>
          <p className="text-xl text-gray-600 mb-8">
            Unlock more features for your family legacy
          </p>
          
          {/* Billing Toggle - Prominent */}
          <div className="inline-flex items-center bg-gradient-to-r from-gray-100 to-gray-200 rounded-full p-1.5 shadow-lg border-2 border-gray-300">
            <button
              onClick={() => setBillingCycle('monthly')}
              data-testid="billing-toggle-monthly"
              className={`px-6 py-3 rounded-full text-base font-semibold transition-all duration-300 ${
                billingCycle === 'monthly'
                  ? 'bg-blue-600 text-white shadow-md transform scale-105'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              data-testid="billing-toggle-yearly"
              className={`px-6 py-3 rounded-full text-base font-semibold transition-all duration-300 flex items-center gap-2 ${
                billingCycle === 'yearly'
                  ? 'bg-blue-600 text-white shadow-md transform scale-105'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
              }`}
            >
              Yearly
              <span className={`text-xs px-2 py-1 rounded-full font-bold ${
                billingCycle === 'yearly' 
                  ? 'bg-green-400 text-green-900' 
                  : 'bg-green-100 text-green-700 animate-pulse'
              }`}>
                Save 17%
              </span>
            </button>
          </div>
        </div>

        {/* Current Usage - Only show for authenticated users */}
        {!isPublic && usage && (
          <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Current Usage</h3>
            <div className="grid grid-cols-3 gap-6">
              <div>
                <p className="text-sm text-gray-500">Families</p>
                <p className="text-2xl font-bold text-gray-900">
                  {usage.usage.families.used}
                  <span className="text-lg text-gray-400 font-normal">
                    /{usage.usage.families.unlimited ? '∞' : usage.usage.families.limit}
                  </span>
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Photos</p>
                <p className="text-2xl font-bold text-gray-900">
                  {usage.usage.photos.used}
                  <span className="text-lg text-gray-400 font-normal">
                    /{usage.usage.photos.unlimited ? '∞' : usage.usage.photos.limit}
                  </span>
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Events</p>
                <p className="text-2xl font-bold text-gray-900">
                  {usage.usage.events.used}
                  <span className="text-lg text-gray-400 font-normal">
                    /{usage.usage.events.unlimited ? '∞' : usage.usage.events.limit}
                  </span>
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Founding Family Badge Promotion */}
        <div className="relative overflow-hidden bg-gradient-to-r from-amber-50 via-yellow-50 to-amber-50 rounded-2xl border-2 border-amber-200 p-6 mb-8" data-testid="founding-family-promo">
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-amber-200/30 to-yellow-200/30 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-amber-200/30 to-yellow-200/30 rounded-full translate-y-1/2 -translate-x-1/2" />
          
          <div className="relative flex flex-col md:flex-row items-center gap-6">
            {/* Badge Display */}
            <div className="flex-shrink-0">
              <div className="relative">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-400 via-yellow-500 to-amber-400 flex items-center justify-center shadow-lg">
                  <span className="text-4xl">🏛️</span>
                </div>
                {/* Animated ring */}
                <div className="absolute inset-0 rounded-full border-2 border-amber-300 animate-ping opacity-30" />
              </div>
            </div>
            
            {/* Content */}
            <div className="flex-1 text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
                <h3 className="text-xl font-bold text-amber-900">Become a Founding Family Member</h3>
                {user?.founding_member && (
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-semibold">You have this!</span>
                )}
              </div>
              <p className="text-amber-800 mb-3">
                Subscribe during our launch period and receive the exclusive <strong>Founding Family Badge</strong> — 
                a permanent mark of honor displayed on your profile, recognizing you as an early supporter of Legacy Forge Family.
              </p>
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-3 text-sm">
                <span className="inline-flex items-center gap-1 text-amber-700">
                  <Check className="w-4 h-4 text-green-600" /> Permanent badge on your profile
                </span>
                <span className="inline-flex items-center gap-1 text-amber-700">
                  <Check className="w-4 h-4 text-green-600" /> Exclusive to early subscribers
                </span>
                <span className="inline-flex items-center gap-1 text-amber-700">
                  <Check className="w-4 h-4 text-green-600" /> Limited time offer
                </span>
              </div>
            </div>
            
            {/* CTA for non-subscribers */}
            {!user?.founding_member && currentSubscription?.tier === 'free' && (
              <div className="flex-shrink-0">
                <button
                  onClick={() => document.getElementById('seedling-card')?.scrollIntoView({ behavior: 'smooth' })}
                  className="px-6 py-3 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                  data-testid="founding-family-cta"
                >
                  Claim Your Badge
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {tierOrder.map((tierKey) => {
            const tier = tiers[tierKey];
            const TierIcon = tierIcons[tierKey];
            const color = tierColors[tierKey];
            const isCurrentTier = currentSubscription?.tier === tierKey;
            const price = billingCycle === 'yearly' ? tier.price_yearly : tier.price_monthly;
            
            return (
              <div
                key={tierKey}
                id={`${tierKey}-card`}
                className={`bg-white rounded-xl border-2 p-6 relative ${
                  isCurrentTier 
                    ? 'border-green-500' 
                    : tierKey === 'growing' 
                      ? 'border-blue-500 ring-2 ring-blue-500 ring-offset-2' 
                      : 'border-gray-200'
                }`}
                data-testid={`tier-card-${tierKey}`}
              >
                {tierKey === 'growing' && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                    MOST POPULAR
                  </div>
                )}
                
                {isCurrentTier && (
                  <div className="absolute -top-3 right-4 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                    CURRENT
                  </div>
                )}

                <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-4 ${
                  tierKey === 'free' ? 'bg-gray-100' :
                  tierKey === 'seedling' ? 'bg-emerald-100' :
                  tierKey === 'growing' ? 'bg-blue-100' :
                  'bg-amber-100'
                }`}>
                  <span className="text-2xl">{tierEmojis[tierKey]}</span>
                </div>

                <h3 className="text-xl font-bold text-gray-900">{tier.name}</h3>
                <p className="text-gray-500 text-sm mt-1 mb-4">{tier.description}</p>

                <div className="mb-6">
                  <span className="text-4xl font-bold text-gray-900">
                    ${price}
                  </span>
                  {tierKey !== 'free' && (
                    <span className="text-gray-500">/{billingCycle === 'yearly' ? 'year' : 'month'}</span>
                  )}
                </div>

                <ul className="space-y-3 mb-6">
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-green-500" />
                    <span>{formatLimit(tier.features.max_families)} {tier.features.max_families === 1 ? 'family' : 'families'}</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-green-500" />
                    <span>{formatLimit(tier.features.max_members_per_family)} members/family</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-green-500" />
                    <span>{formatLimit(tier.features.max_photos)} photos</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-green-500" />
                    <span>{formatLimit(tier.features.max_events)} events</span>
                  </li>
                  
                  {/* Tier-specific features */}
                  {tierKey === 'free' && (
                    <>
                      <li className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-green-500" />
                        <span>Family stories & timeline</span>
                      </li>
                      <li className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-green-500" />
                        <span>Chat & messaging</span>
                      </li>
                    </>
                  )}
                  
                  {tierKey === 'seedling' && (
                    <>
                      <li className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-green-500" />
                        <span>Spiritual features</span>
                      </li>
                      <li className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-green-500" />
                        <span>Role assignments</span>
                      </li>
                      <li className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-green-500" />
                        <span>Album sharing (view)</span>
                      </li>
                      <li className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-green-500" />
                        <span>Email notifications</span>
                      </li>
                    </>
                  )}
                  
                  {tierKey === 'growing' && (
                    <>
                      <li className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-green-500" />
                        <span>Everything in Seedling</span>
                      </li>
                      <li className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-green-500" />
                        <span>Priority messages &amp; pinned announcements</span>
                      </li>
                      <li className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-green-500" />
                        <span>Dues management</span>
                      </li>
                      <li className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-green-500" />
                        <span>Voting & governance</span>
                      </li>
                      <li className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-green-500" />
                        <span>Album downloads</span>
                      </li>
                    </>
                  )}
                  
                  {tierKey === 'flourishing' && (
                    <>
                      <li className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-green-500" />
                        <span>Everything in Growing</span>
                      </li>
                      <li className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-green-500" />
                        <span>Professional resources</span>
                      </li>
                      <li className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-green-500" />
                        <span>Family analytics</span>
                      </li>
                      <li className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-green-500" />
                        <span>Data export & priority support</span>
                      </li>
                    </>
                  )}
                </ul>

                {!isPublic && isCurrentTier ? (
                  <button
                    disabled
                    className="w-full py-3 bg-gray-100 text-gray-500 rounded-lg font-medium cursor-not-allowed"
                  >
                    Current Plan
                  </button>
                ) : tierKey === 'free' ? (
                  <button
                    onClick={() => isPublic ? navigate('/register') : null}
                    className={`w-full py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                      isPublic 
                        ? 'bg-gray-800 text-white hover:bg-gray-900' 
                        : 'bg-gray-100 text-gray-500 cursor-not-allowed'
                    }`}
                    data-testid="get-started-free-btn"
                  >
                    {isPublic ? (
                      <>
                        Get Started Free
                        <ChevronRight className="w-4 h-4" />
                      </>
                    ) : (
                      'Free Forever'
                    )}
                  </button>
                ) : (
                  <button
                    onClick={() => handleSubscribe(tierKey)}
                    disabled={processingTier === tierKey}
                    className={`w-full py-3 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                      tierKey === 'seedling' ? 'bg-emerald-600 hover:bg-emerald-700' :
                      tierKey === 'growing' ? 'bg-blue-600 hover:bg-blue-700' :
                      'bg-amber-600 hover:bg-amber-700'
                    }`}
                    data-testid={`subscribe-btn-${tierKey}`}
                  >
                    {processingTier === tierKey ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        {isPublic ? `Choose ${tier.name}` : `Upgrade to ${tier.name}`}
                        <ChevronRight className="w-4 h-4" />
                      </>
                    )}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Subscription Management - Only for authenticated users */}
        {!isPublic && currentSubscription?.tier !== 'free' && (
          <div className="mt-8 bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Subscription Management
            </h3>
            
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600">
                  {currentSubscription.cancel_at_period_end ? (
                    <span className="text-amber-600">
                      Your subscription will end at the current billing period
                    </span>
                  ) : (
                    <span>
                      Your {currentSubscription.tier_info?.name} subscription is active
                    </span>
                  )}
                </p>
                {currentSubscription.current_period_end && (
                  <p className="text-sm text-gray-500 mt-1">
                    Next billing date: {new Date(currentSubscription.current_period_end).toLocaleDateString()}
                  </p>
                )}
              </div>
              
              <div className="flex gap-3">
                {currentSubscription.cancel_at_period_end ? (
                  <button
                    onClick={handleReactivate}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    Reactivate
                  </button>
                ) : (
                  <button
                    onClick={handleCancelSubscription}
                    className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50"
                  >
                    Cancel Subscription
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Security Note */}
        <div className="mt-8 text-center">
          <div className="inline-flex items-center gap-2 text-gray-500 text-sm">
            <Shield className="w-4 h-4" />
            <span>Secure payment powered by Stripe</span>
          </div>
        </div>

        {/* Back Button / CTA */}
        <div className="mt-8 text-center">
          {isPublic ? (
            <div className="space-y-4">
              <p className="text-gray-600">Already have an account?</p>
              <button
                onClick={() => navigate('/login')}
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                Sign in to manage your subscription →
              </button>
            </div>
          ) : (
            <button
              onClick={() => navigate('/dashboard')}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              ← Back to Dashboard
            </button>
          )}
        </div>
      </div>
      </div>
    </div>
  );
}
